import { useState, useEffect } from "react";
import { apiJoomla } from "@/services/api";

// Define a estrutura dos itens do menu
interface ItemMenu {
    id: number;
    title: string;
    uri: string;
    selected: boolean;
    alias: string;
    note: string;
    path: string;
    link: string;
    type: string;
    level: number;
    childs: ItemMenu[];
}

// Tipos possíveis para o menu
type TMenu =
    'menu-feagri' |
    'feagri-graduacao-academi' |
    'menu-feagri-pesquisa' |
    'feagri-graduacao-estude' |
    'feagri-posgraduacao-estu' |
    'feagri-posgraduacao-acad' |
    'feagri-extensao-academic';

// Propriedades aceitas pelo componente MenuLateral
interface MenuLateralProps {
    title: string;
    menuType: TMenu;
}

// Componente MenuLateral
export function MenuLateral({ menuType, title }: MenuLateralProps) {
    // Estado para armazenar os itens do menu
    const [menuItems, setMenuItems] = useState<ItemMenu[]>([]);

    // Estado para armazenar o ID do menu atualmente expandido
    const [expandedMenuId, setExpandedMenuId] = useState<number | null>(null);

    // Estado para armazenar os IDs dos itens filhos expandidos
    const [expandedChildIds, setExpandedChildIds] = useState<Set<number>>(new Set());

    // Efeito para buscar os itens do menu da API quando o menuType mudar
    useEffect(() => {
        const fetchMenuItems = async () => {
            try {
                const response = await apiJoomla.get(`/jmenu/alltype?menu=${menuType}`);
                setMenuItems(response.data.menus);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (err: any) {
                if (err.response) {
                    console.error(`Erro: ${err.response}`);
                } else {
                    console.error('Erro: tente mais tarde !!!');
                }
            }
        };

        fetchMenuItems();
    }, [menuType]);

    // Função para alternar a expansão do menu principal
    const handleMenuToggle = (menuId: number) => {
        setExpandedMenuId(prevId => (prevId === menuId ? null : menuId));
    };

    // Função para alternar a expansão dos itens filhos
    const handleChildToggle = (_parentId: number, childId: number) => {
        setExpandedChildIds(prevIds => {
            const newIds = new Set(prevIds);
            if (newIds.has(childId)) {
                newIds.delete(childId);
            } else {
                newIds.add(childId);
            }
            return newIds;
        });
    };

    return (
        <aside id="lateral" className="lateral d-none d-sm-block col-lg-3 col-md-4">
            {/* Título do menu lateral */}
            <h2 className="text-2xl font-normal mb-2 uppercase">{title}</h2>

            {/* Navegação do menu lateral */}
            <nav className="menu-lateral">
                <ul>
                    {/* Mapeia cada item do menu principal */}
                    {menuItems.map((menuItem) => {
                        if (menuItem.childs.length > 0) {
                            // Se o item do menu tiver filhos
                            return (
                                <li className="item-parent has-child duration-75" key={menuItem.id}>
                                    {/* Ícone para expandir/contrair o item do menu */}
                                    <span
                                        className={`far ${expandedMenuId === menuItem.id ? 'fa-minus-square' : 'fa-plus-square'}`}
                                        onClick={() => handleMenuToggle(menuItem.id)}
                                    ></span>

                                    {/* Link do item do menu */}
                                    <a href={
                                        menuItem.path
                                            ? (menuItem.uri ? `${menuItem.uri}${menuItem.path}` : `https://www.feagri.unicamp.br/portal/${menuItem.path}`)
                                            : menuItem.uri
                                    }>{menuItem.title}</a>

                                    {/* Submenu do item do menu */}
                                    <ul className={`nested ${expandedMenuId === menuItem.id ? 'active' : ''}`}>
                                        {/* Mapeia cada item filho do item do menu */}
                                        {menuItem.childs.map((childItem) => {
                                            if (childItem.childs.length > 0) {
                                                // Se o item filho tiver filhos
                                                return (
                                                    <li className="item-parent has-child" key={childItem.id}>
                                                        {/* Ícone para expandir/contrair o item filho */}
                                                        <span
                                                            className={`far ${expandedChildIds.has(childItem.id) ? 'fa-minus-square' : 'fa-plus-square'}`}
                                                            onClick={() => handleChildToggle(menuItem.id, childItem.id)}
                                                        ></span>

                                                        {/* Link do item filho */}
                                                        <a className="font-semibold" href={
                                                            childItem.path
                                                                ? (childItem.uri ? `${childItem.uri}${childItem.path}` : `https://www.feagri.unicamp.br/portal/${childItem.path}`)
                                                                : childItem.uri
                                                        }>{childItem.title}</a>

                                                        {/* Submenu do item filho */}
                                                        <ul className={`nested ${expandedChildIds.has(childItem.id) ? 'active' : ''}`}>
                                                            {/* Mapeia cada item sub-filho do item filho */}
                                                            {childItem.childs.map((subChildItem) => {
                                                                if (subChildItem.childs.length > 0) {
                                                                    // Se o item sub-filho tiver filhos
                                                                    return (
                                                                        <li className="item-parent has-child" key={subChildItem.id}>
                                                                            {/* Ícone para expandir/contrair o item sub-filho */}
                                                                            <span
                                                                                className={`far ${expandedChildIds.has(subChildItem.id) ? 'fa-minus-square' : 'fa-plus-square'}`}
                                                                                onClick={() => handleChildToggle(menuItem.id, subChildItem.id)}
                                                                            ></span>

                                                                            {/* Link do item sub-filho */}
                                                                            <a className="font-semibold" href={
                                                                                subChildItem.path
                                                                                    ? (subChildItem.uri ? `${subChildItem.uri}${subChildItem.path}` : `https://www.feagri.unicamp.br/portal/${subChildItem.path}`)
                                                                                    : subChildItem.uri
                                                                            }>{subChildItem.title}</a>

                                                                            {/* Submenu do item sub-filho */}
                                                                            <ul className={`nested ${expandedChildIds.has(subChildItem.id) ? 'active' : ''}`}>
                                                                                {/* Mapeia cada item bisneto do item sub-filho */}
                                                                                {subChildItem.childs.map(grandChildItem => (
                                                                                    <li key={grandChildItem.id}>
                                                                                        {/* Link do item bisneto */}
                                                                                        {grandChildItem.type === 'url' ? (
                                                                                            <a href={grandChildItem.link}>{grandChildItem.title}</a>
                                                                                        ) : (
                                                                                            <a href={grandChildItem.uri + grandChildItem.path}>{grandChildItem.title}</a>
                                                                
                                                                                        )}
                                                                                    </li>
                                                                                ))}
                                                                            </ul>
                                                                        </li>
                                                                    );
                                                                } else {
                                                                    // Se o item sub-filho não tiver filhos
                                                                    return (
                                                                        <li key={subChildItem.id}>
                                                                            {/* Link do item sub-filho */}
                                                                        {subChildItem.type === 'url' ? (
                                                                            <a href={subChildItem.link}>{subChildItem.title}</a>
                                                                        ) : (
                                                                            <a href={subChildItem.uri + subChildItem.path}>{subChildItem.title}</a>
                                                                        )}
                                                                        </li>
                                                                    );
                                                                }
                                                            })}
                                                        </ul>
                                                    </li>
                                                );
                                            } else {
                                                // Se o item filho não tiver filhos
                                                return (
                                                    <li key={childItem.id}>
                                                        {/* Verifica o tipo do item filho e renderiza o link apropriado */}
                                                        {childItem.type === 'url'
                                                            ? <a href={childItem.link}>{childItem.title}</a>
                                                            : <a href={childItem.uri + childItem.path}>{childItem.title}</a>
                                                        }
                                                    </li>
                                                );
                                            }
                                        })}
                                    </ul>
                                </li>
                            );
                        } else {
                            // Se o item do menu não tiver filhos
                            return (
                                <li className="item-parent has-child" key={menuItem.id}>
                                    {/* Verifica se o item do menu tem um URI ou link e renderiza o link apropriado */}
                                    {menuItem.uri ? (
                                        <a href={menuItem.uri + menuItem.path}>{menuItem.title}</a>
                                    ) : (
                                        <a target="_blank" href={menuItem.link}>{menuItem.title}</a>
                                    )}
                                </li>
                            );
                        }
                    })}
                </ul>
            </nav>
        </aside>

    );
}
